import mixpanel, { type Dict } from 'mixpanel-browser'
import type React from 'react'
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react'

interface MixpanelContextProps {
  trackEvent: (event: string, props?: Dict) => void
}

const MixpanelContext = createContext<MixpanelContextProps>({ trackEvent: () => {} })

interface MixpanelProviderProps {
  mixpanelKey: string | undefined
  children: ReactNode
}

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({ mixpanelKey, children }) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && mixpanelKey) {
      mixpanel.init(mixpanelKey, { track_pageview: true, debug: process.env.NODE_ENV !== 'production' })
    }
  }, [mixpanelKey])

  const trackEvent = useCallback(
    (event: string, props?: Dict) => {
      if (mixpanelKey) {
        mixpanel.track(event, props)
      }
    },
    [mixpanelKey]
  )

  const value = useMemo(() => ({ trackEvent }), [trackEvent])

  return <MixpanelContext.Provider value={value}>{children}</MixpanelContext.Provider>
}

export const useMixpanel = () => {
  const context = useContext(MixpanelContext)
  if (!context) {
    throw new Error('useMixpanel must be used within a MixpanelProvider')
  }
  return context
}
